import {
  useCurrentWorkspace,
  useSetWorkspaceEmailAutoResponderText,
  useSetWorkspaceEmailEnabled,
  useSetWorkspacePhoneNumber,
  useSetWorkspaceSmsEnabled,
  useWorkspaceEmailSettings,
  useWorkspacePhoneNumber,
  useWorkspaceSmsEnabled,
} from '@gripp/shared-logic';
import {
  Button,
  Colors,
  CustomAlert,
  Spacing,
  TextInput as TextInputCropCircle,
  typography,
  useSlideLayout,
} from '@gripp/shared-ui';
import { useCallback, useEffect, useRef, useState } from 'react';
import Toast from 'react-native-toast-message';
import { useTranslation } from 'react-i18next';
import {
  ActivityIndicator,
  Pressable,
  ScrollView,
  StyleSheet,
  Text,
  View,
} from 'react-native';

// Matches the SendGrid Inbound Parse host configured for the Communication
// platform (plan 0024 Phase 1) — `<slug>@mail.gripp.it`.
const EMAIL_DOMAIN = 'mail.gripp.it';

export const SettingsCommunication = () => {
  const { setColumnTitle } = useSlideLayout();
  const { t } = useTranslation();
  const currentWorkspace = useCurrentWorkspace();
  const workspaceId = currentWorkspace?.id;

  const { emailEnabled, emailSlug, emailAutoResponderText, isLoading, refetch } =
    useWorkspaceEmailSettings(workspaceId);
  const { mutateAsync: setEmailEnabled, isPending } =
    useSetWorkspaceEmailEnabled();
  const {
    mutateAsync: setEmailAutoResponderText,
    isPending: isSavingAutoResponder,
  } = useSetWorkspaceEmailAutoResponderText();

  const { phoneNumber, isLoading: isPhoneLoading, refetch: refetchPhone } =
    useWorkspacePhoneNumber(workspaceId);
  const { mutateAsync: setPhoneNumber, isPending: isSavingPhone } =
    useSetWorkspacePhoneNumber();

  const { smsEnabled, isLoading: isSmsLoading, refetch: refetchSms } =
    useWorkspaceSmsEnabled(workspaceId);
  const { mutateAsync: setSmsEnabled, isPending: isSavingSms } =
    useSetWorkspaceSmsEnabled();

  const [copied, setCopied] = useState(false);
  // Tracks the "Copied" reset timer so it can be cleared on unmount (avoids a
  // setState-after-unmount) and replaced if copy is pressed again mid-window.
  const copyResetRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(
    () => () => {
      if (copyResetRef.current) clearTimeout(copyResetRef.current);
    },
    []
  );
  const [confirmDisableVisible, setConfirmDisableVisible] = useState(false);
  const [confirmRemovePhoneVisible, setConfirmRemovePhoneVisible] =
    useState(false);
  // Local draft so keystrokes don't round-trip through the server on every
  // change — committed with the explicit Save button (see handleAutoResponderSave).
  const [autoResponderDraft, setAutoResponderDraft] = useState('');
  // Only used while unset — once phoneNumber is saved the section switches to
  // a display + Remove button, mirroring the email address display.
  const [phoneDraft, setPhoneDraft] = useState('');

  useEffect(() => {
    setAutoResponderDraft(emailAutoResponderText ?? '');
  }, [emailAutoResponderText]);

  // The draft differs from what's stored — enables the Save button.
  const autoResponderDirty = autoResponderDraft !== (emailAutoResponderText ?? '');

  useEffect(() => {
    setColumnTitle(t('preferences.settings.communication'));
  }, [setColumnTitle, t]);

  const emailAddress = emailSlug ? `${emailSlug}@${EMAIL_DOMAIN}` : null;

  const handleEnable = useCallback(async () => {
    if (!workspaceId) return;
    try {
      await setEmailEnabled({ workspaceId, enabled: true });
      await refetch();
    } catch {
      Toast.show({ type: 'error', text1: t('communication.enableError') });
    }
  }, [workspaceId, setEmailEnabled, refetch, t]);

  const handleDisable = useCallback(async () => {
    if (!workspaceId) return;
    setConfirmDisableVisible(false);
    try {
      await setEmailEnabled({ workspaceId, enabled: false });
      await refetch();
    } catch {
      Toast.show({ type: 'error', text1: t('communication.disableError') });
    }
  }, [workspaceId, setEmailEnabled, refetch, t]);

  const handleAutoResponderSave = useCallback(async () => {
    if (!workspaceId || !autoResponderDirty) return;
    try {
      await setEmailAutoResponderText({
        workspaceId,
        emailAutoResponderText: autoResponderDraft,
      });
      await refetch();
      Toast.show({
        type: 'success',
        text1: t('communication.autoResponderSaved'),
      });
    } catch {
      Toast.show({ type: 'error', text1: t('communication.autoResponderError') });
    }
  }, [
    workspaceId,
    autoResponderDirty,
    autoResponderDraft,
    setEmailAutoResponderText,
    refetch,
    t,
  ]);

  const handleSavePhone = useCallback(async () => {
    if (!workspaceId || !phoneDraft.trim()) return;
    try {
      await setPhoneNumber({ workspaceId, phoneNumber: phoneDraft.trim() });
      setPhoneDraft('');
      await refetchPhone();
    } catch (error) {
      const alreadyInUse =
        error instanceof Error && /already in use/i.test(error.message);
      Toast.show({
        type: 'error',
        text1: alreadyInUse
          ? t('communication.phone.alreadyInUse')
          : t('communication.phone.saveError'),
      });
    }
  }, [workspaceId, phoneDraft, setPhoneNumber, refetchPhone, t]);

  const handleRemovePhone = useCallback(async () => {
    if (!workspaceId) return;
    setConfirmRemovePhoneVisible(false);
    try {
      await setPhoneNumber({ workspaceId, phoneNumber: null });
      await refetchPhone();
    } catch {
      Toast.show({ type: 'error', text1: t('communication.phone.removeError') });
    }
  }, [workspaceId, setPhoneNumber, refetchPhone, t]);

  const handleToggleSms = useCallback(async () => {
    if (!workspaceId) return;
    try {
      await setSmsEnabled({ workspaceId, enabled: !smsEnabled });
      await refetchSms();
    } catch {
      Toast.show({
        type: 'error',
        text1: smsEnabled
          ? t('communication.sms.disableError')
          : t('communication.sms.enableError'),
      });
    }
  }, [workspaceId, smsEnabled, setSmsEnabled, refetchSms, t]);

  const handleCopy = useCallback(async () => {
    if (emailAddress) {
      try {
        await navigator.clipboard.writeText(emailAddress);
        setCopied(true);
        if (copyResetRef.current) clearTimeout(copyResetRef.current);
        copyResetRef.current = setTimeout(() => setCopied(false), 2000);
      } catch {
        Toast.show({ type: 'error', text1: t('communication.copyError') });
      }
    }
  }, [emailAddress, t]);

  if (!workspaceId || isLoading || isPhoneLoading || isSmsLoading) {
    return (
      <View style={styles.loadingContainer}>
        <ActivityIndicator size="large" color={Colors.primary} />
      </View>
    );
  }

  return (
    <View style={styles.container}>
      <ScrollView contentContainerStyle={styles.contentContainer}>
        <Text style={typography.title.smallEmphasised}>
          {t('preferences.settings.communication')}
        </Text>
        <Text style={[typography.body.small, styles.description]}>
          {t('communication.description')}
        </Text>

        {emailEnabled && emailAddress && (
          <View style={styles.existingKey}>
            <View style={styles.keyInfoRow}>
              <Text style={typography.label.medium}>
                {t('communication.emailAddress')}
              </Text>
              <Pressable
                onPress={handleCopy}
                accessibilityRole="button"
                accessibilityLabel={t('communication.emailAddress')}
              >
                <Text style={[typography.body.small, styles.copyableText]}>
                  {copied ? t('communication.copied') : emailAddress}
                </Text>
              </Pressable>
            </View>
          </View>
        )}

        {emailEnabled && (
          <View style={styles.autoResponderSection}>
            <TextInputCropCircle
              label={t('communication.autoResponderLabel')}
              value={autoResponderDraft}
              onChangeText={setAutoResponderDraft}
              textArea
              numberOfLines={5}
              placeholder={t('communication.autoResponderPlaceholder')}
              testID="communication-auto-responder-text"
            />
            <Text style={[typography.body.small, styles.description]}>
              {t('communication.autoResponderHelp')}
            </Text>
            <Button
              onPress={handleAutoResponderSave}
              state={
                autoResponderDirty && !isSavingAutoResponder
                  ? 'active'
                  : 'default'
              }
              size="large"
              disabled={!autoResponderDirty || isSavingAutoResponder}
              style={styles.actionButton}
              testID="communication-auto-responder-save"
            >
              {isSavingAutoResponder ? (
                <ActivityIndicator size="small" color={Colors.white} />
              ) : (
                t('communication.autoResponderSave')
              )}
            </Button>
          </View>
        )}

        <View style={styles.phoneSection}>
          <Text style={typography.title.smallEmphasised}>
            {t('communication.phone.title')}
          </Text>
          <Text style={[typography.body.small, styles.description]}>
            {t('communication.phone.description')}
          </Text>

          {phoneNumber ? (
            <View style={styles.existingKey}>
              <View style={styles.keyInfoRow}>
                <Text style={typography.label.medium}>
                  {t('communication.phone.title')}
                </Text>
                <Text style={typography.body.small}>{phoneNumber}</Text>
              </View>
            </View>
          ) : (
            <TextInputCropCircle
              label={t('communication.phone.title')}
              value={phoneDraft}
              onChangeText={setPhoneDraft}
              placeholder={t('communication.phone.placeholder')}
              testID="communication-phone-input"
            />
          )}

          <Button
            onPress={
              phoneNumber
                ? () => setConfirmRemovePhoneVisible(true)
                : handleSavePhone
            }
            state={
              isSavingPhone
                ? 'default'
                : phoneNumber || phoneDraft.trim()
                  ? 'active'
                  : 'default'
            }
            size="large"
            disabled={
              isSavingPhone || (!phoneNumber && !phoneDraft.trim())
            }
            style={styles.actionButton}
            testID="communication-phone-action-button"
          >
            {isSavingPhone ? (
              <ActivityIndicator size="small" color={Colors.white} />
            ) : phoneNumber ? (
              t('communication.phone.remove')
            ) : (
              t('communication.phone.save')
            )}
          </Button>

          {phoneNumber && (
            <View style={styles.smsSection}>
              <Text style={typography.label.medium}>
                {t('communication.sms.title')}
              </Text>
              <Text style={[typography.body.small, styles.description]}>
                {t('communication.sms.description')}
              </Text>
              <Button
                onPress={handleToggleSms}
                state={isSavingSms ? 'default' : 'active'}
                disabled={isSavingSms}
                size="large"
                style={styles.actionButton}
                testID="communication-sms-action-button"
              >
                {isSavingSms ? (
                  <ActivityIndicator size="small" color={Colors.white} />
                ) : smsEnabled ? (
                  t('communication.sms.disable')
                ) : (
                  t('communication.sms.enable')
                )}
              </Button>
            </View>
          )}
        </View>
      </ScrollView>

      <View style={styles.contentBottomBar}>
        <Button
          onPress={
            emailEnabled ? () => setConfirmDisableVisible(true) : handleEnable
          }
          state={isPending ? 'default' : 'active'}
          disabled={isPending}
          size="large"
          style={styles.actionButton}
        >
          {isPending ? (
            <ActivityIndicator size="small" color={Colors.white} />
          ) : emailEnabled ? (
            t('communication.disable')
          ) : (
            t('communication.enable')
          )}
        </Button>
      </View>

      <CustomAlert
        visible={confirmDisableVisible}
        hideAlert={() => setConfirmDisableVisible(false)}
        action={handleDisable}
        title={t('communication.disableConfirm')}
        primaryButtonLabel={t('communication.disableConfirmYes')}
        secondaryButtonLabel={t('topNav.customAlert.cancel')}
      />

      <CustomAlert
        visible={confirmRemovePhoneVisible}
        hideAlert={() => setConfirmRemovePhoneVisible(false)}
        action={handleRemovePhone}
        title={t('communication.phone.removeConfirm')}
        primaryButtonLabel={t('communication.phone.removeConfirmYes')}
        secondaryButtonLabel={t('topNav.customAlert.cancel')}
      />
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  loadingContainer: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
  },
  contentContainer: {
    paddingVertical: Spacing.basePadding.paddingVertical,
    paddingHorizontal: Spacing.basePadding.paddingHorizontal,
    gap: 16,
  },
  description: {
    color: Colors.secondaryText,
  },
  existingKey: {
    backgroundColor: Colors.primaryGrayBackground,
    padding: 16,
    borderRadius: 8,
    gap: 8,
  },
  autoResponderSection: {
    gap: 8,
  },
  phoneSection: {
    gap: 8,
  },
  smsSection: {
    gap: 8,
    marginTop: 8,
  },
  keyInfoRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  copyableText: {
    fontFamily: 'monospace',
    color: Colors.primary,
  },
  contentBottomBar: {
    padding: Spacing.baseMargin.base,
    backgroundColor: Colors.white,
  },
  actionButton: {
    borderRadius: 2,
    width: '100%',
    height: 48,
  },
});
